import { Results, FilterType } from "types/types";

/**
 * Function to update the filterVariants of a results object to specified values.
 * If no specification given, original results are returned
 * @param results Results to perform filter on
 * @param filterVariantSpecifications Specification for each no column, to change filter variant
 * @returns Results object with adjusted filterVariant fields
 */
export function setFilterVariants(results: Results, filterVariantSpecifications: {[key: number]: FilterType} | null): Results {
  if(filterVariantSpecifications === null) return results;

  const newResults: Results = {
    ...results,
    fields: results.fields.map((field, index) => ({
      ...field,
      filterVariant: filterVariantSpecifications[index] ?? field.filterVariant,
    })),
  };

  return newResults;
}

/**
 * Function to filter included columns based on user input.
 * If no columns are specified, all original columns will be included.
 * @param results Results to perform filter on
 * @param includedColumns Array of included columns, e.g. [1, 4, 7]
 * @returns filtered Results object
 */
export function filterIncludedColumns(results: Results, includedColumns: number[]): Results {
  if(includedColumns.length === 0) return results;
  
  const isIncluded = (_, index) => {
    return includedColumns.includes(index);
  };

  const newResults: Results = {
    data: results.data.map((row) =>
      Object.fromEntries(
        Object.entries(row).filter(isIncluded),
      ),
    ),
    fields: results.fields.filter(isIncluded)
  };

  return newResults;
}

/**
 * Function to filter initial visible columns based on user input. Will filter according to original fields.
 * If no columns are specified, all original columns will be visible.
 * @param results Results to perform filter on. Should match what is originally put into other filters
 * @param visibleColumns Array of visible columns, e.g. [1, 7]
 * @returns object with filtered fields and their visibility
 */
export function filterInitialVisibleColumns(originalResults: Results, visibleColumns: number[]): Record<string, boolean> {
  const showAll: boolean = visibleColumns.length === 0;
  const visibility: Record<string, boolean> = {};

  originalResults.fields.forEach((field, index) => {
    visibility[field.name] = showAll || visibleColumns.includes(index);
  });

  return visibility;
}
